// ImagesManager.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/**
 * Props:
 *  - apiKey (string)   : required (X-API-Key)
 *  - baseURL (string)  : default https://rankridgeschools.com/rankridgeschool/dev/v1
 */
export default function ImagesManager({
  apiKey="12345678",
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [apiKey, baseURL]
  );

  // list state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // filters/paging
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [pageId, setPageId] = useState("");
  const [subpageId, setSubpageId] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);

  // add/edit modal
  const [editing, setEditing] = useState(null); // null => add mode; object => edit item
  const [saving, setSaving] = useState(false);

  // upload
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // lookup lists for selects
  const [pagesList, setPagesList] = useState([]);       // [{page_id, page_name, ...}]
  const [subpagesList, setSubpagesList] = useState([]); // [{subpage_id, subpage_name, page_id, ...}]

  /* ============ LOADERS ============ */
  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/images", {
        params: {
          search,
          status,
          category,
          page_id: pageId || undefined,
          subpage_id: subpageId || undefined,
          page,
          limit,
          sort: "updated_date",
          order: "desc",
        },
      });
      if (data?.success) {
        setItems(data.items || []);
        setTotal(data.pagination?.total || 0);
      } else {
        setErr(data?.message || "Failed to load");
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Load error");
    } finally {
      setLoading(false);
    }
  }

  async function loadPages() {
    try {
      const { data } = await api.get("/pages", {
        params: { status: "active", sort: "page_name", order: "asc", limit: 200 },
      });
      setPagesList(data?.items || []);
    } catch {
      setPagesList([]);
    }
  }

  async function loadSubpagesByPage(pid) {
    if (!pid) {
      setSubpagesList([]);
      return;
    }
    try {
      const { data } = await api.get("/subpages", {
        params: { page_id: pid, status: "active", sort: "subpage_name", order: "asc", limit: 500 },
      });
      setSubpagesList(data?.items || []);
    } catch {
      setSubpagesList([]);
    }
  }

  /* ============ EFFECTS ============ */
  useEffect(() => { load(); /* initial */ }, []); // eslint-disable-line
  useEffect(() => { load(); /* on filters */ }, [search, status, category, pageId, subpageId, page, limit]); // eslint-disable-line

  // preload page list for filters bar (optional, comment out if not needed for filters)
  useEffect(() => { loadPages(); }, []); // eslint-disable-line

  const pagesCount = Math.max(1, Math.ceil(total / limit));

  /* ============ ADD / EDIT ============ */
  function openAdd() {
    setEditing({
      image_title: "",
      image_alt: "",
      image_path: "",
      page_id: "",
      subpage_id: "",
      category: "",
      status: "active",
    });
    // ensure lists ready
    loadPages();
    setSubpagesList([]);
  }

  function openEdit(item) {
    setEditing({ ...item });
    // make sure pages + subpages are ready
    loadPages().then(() => {
      if (item.page_id) loadSubpagesByPage(item.page_id);
      else setSubpagesList([]);
    });
  }

  // when modal's page_id changes, reload subpages list and clear subpage_id if page changed
  useEffect(() => {
    if (!editing) return;
    const pid = editing.page_id ? Number(editing.page_id) : null;
    loadSubpagesByPage(pid);
    // If selected subpage doesn't belong to new page, clear it
    setEditing(s => {
      if (!s) return s;
      if (!pid) return { ...s, subpage_id: "" };
      const found = subpagesList.find(sp => sp.subpage_id === Number(s.subpage_id));
      return found ? s : { ...s, subpage_id: "" };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.page_id]);

//   async function uploadFile() {
//     const f = fileRef.current?.files?.[0];
//     if (!f) return alert("Choose a file");
//     setUploading(true);
//     try {
//       const fd = new FormData();
//       fd.append("file", f);
//       const { data } = await api.post("/upload2", fd);
//       if (data?.success && data?.path) {
//         setEditing((s) => ({ ...s, image_path: data.path }));
//       } else {
//         alert(data?.message || "Upload failed");
//       }
//     } catch (e) {
//       alert(e?.response?.data?.message || e.message || "Upload error");
//     } finally {
//       setUploading(false);
//       if (fileRef.current) fileRef.current.value = ""; // reset
//     }
//   }


async function uploadFile() {
  const f = fileRef.current?.files?.[0];
  if (!f) return alert("Choose a file");
  setUploading(true);
  try {
    const fd = new FormData();
    fd.append("file", f);
    if (editing.image_path.trim()) fd.append("name", editing.image_path.trim());  // <-- custom base name
    // optional: choose a subdir based on category or page
    if (editing?.category) fd.append("dir", editing.category);       // e.g. "banner"
    // or a deeper path:
    // if (editing?.page_id) fd.append("dir", `pages/${editing.page_id}`);

    const { data } = await api.post("/upload2", fd);
    if (data?.success && data?.path) {
      setEditing((s) => ({ ...s, image_path: data.path }));
    } else {
      alert(data?.message || "Upload failed");
    }
  } catch (e) {
    alert(e?.response?.data?.message || e.message || "Upload error");
  } finally {
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  }
}


  async function save() {
    if (!editing) return;
    const payload = {
      image_title: editing.image_title,
      image_alt: editing.image_alt || null,
      image_path: editing.image_path,
      page_id: editing.page_id ? Number(editing.page_id) : null,
      subpage_id: editing.subpage_id ? Number(editing.subpage_id) : null,
      category: editing.category || null,
      status: editing.status || "active",
    };
    if (!payload.image_title || !payload.image_path) {
      alert("Please provide image title and upload/select image.");
      return;
    }
    setSaving(true);
    try {
      if (editing.image_id) {
        await api.put(`/images/${editing.image_id}`, payload);
      } else {
        await api.post(`/images`, payload, { headers: { "Content-Type": "application/json" } });
      }
      setEditing(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(image_id) {
    if (!window.confirm("Delete this image record? This cannot be undone.")) return;
    try {
      await api.delete(`/images/${image_id}`);
      setItems((prev) => prev.filter((x) => x.image_id !== image_id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  }

  async function patchStatus(image_id, next) {
    try {
      await api.patch(`/images/${image_id}`, { field: "status", value: next });
      setItems((prev) =>
        prev.map((x) => (x.image_id === image_id ? { ...x, status: next } : x))
      );
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Update failed");
    }
  }

  return (
    <div style={wrap}>
      <h2 style={{ margin: 0 }}>Images</h2>

      {/* Filters */}
      <div style={toolbar}>
        <input
          placeholder="Search title/alt/path"
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          style={input}
        />
        <select value={status} onChange={(e)=>{ setPage(1); setStatus(e.target.value); }} style={input}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>
        <input placeholder="Category (e.g. banner)" value={category} onChange={(e)=>{ setPage(1); setCategory(e.target.value); }} style={input}/>
        <input placeholder="Page ID" value={pageId} onChange={(e)=>{ setPage(1); setPageId(e.target.value); }} style={{...input, width:110}}/>
        <input placeholder="Subpage ID" value={subpageId} onChange={(e)=>{ setPage(1); setSubpageId(e.target.value); }} style={{...input, width:130}}/>
        <select value={limit} onChange={(e)=>{ setPage(1); setLimit(Number(e.target.value)); }} style={input}>
          {[8,12,24,48].map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
        <button onClick={load} style={btn("#1f2937","#fff")}>Refresh</button>
        <button onClick={openAdd} style={btn("#16a34a","#fff")}>Add Image</button>
      </div>

      {/* Grid */}
      {loading ? (
        <p>Loading…</p>
      ) : err ? (
        <p style={{ color:"#dc2626" }}>{err}</p>
      ) : items.length === 0 ? (
        <div style={emptyBox}>No images found.</div>
      ) : (
        <>
          <div style={grid}>
            {items.map((it) => (
              <article key={it.image_id} style={card}>
                <div style={{ display:"flex", gap:12 }}>
                  <div style={thumb}>
                    {it.image_path ? (
                      <img src={"https://gantasoft.in/rankridgeschool/dev/"+it.image_path} alt={it.image_alt || it.image_title} style={img} />
                    ) : (
                      <div style={noimg}>No image</div>
                    )}
                  </div>

                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                      <h3 style={{ margin:0, fontSize:16 }}>{it.image_title}</h3>
                      {it.category && <span style={chip}>{it.category}</span>}
                      {it.status && (
                        <span style={{ ...chip, background: it.status === 'active' ? '#dcfce7' : it.status === 'inactive' ? '#fee2e2' : '#e5e7eb', color:'#111' }}>
                          {it.status}
                        </span>
                      )}
                    </div>

                    <div style={{ marginTop:6, color:"#374151", fontSize:13 }}>
                      <div><strong>Path:</strong> <code style={code}>{it.image_path}</code></div>
                      {it.image_alt && <div style={{ marginTop:4 }}><strong>Alt:</strong> {it.image_alt}</div>}
                      <div style={{ marginTop:6, fontSize:12, color:"#6b7280" }}>
                        {it.page_id ? <>page_id: {it.page_id} · </> : null}
                        {it.subpage_id ? <>subpage_id: {it.subpage_id} · </> : null}
                        <span>Updated: {it.updated_date?.replace("T"," ").replace("Z","") || "-"}</span>
                      </div>
                    </div>

                    <div style={{ marginTop:10, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                      <button onClick={() => openEdit(it)} style={btn("#2563eb","#fff")}>Edit</button>
                      <button onClick={() => onDelete(it.image_id)} style={btn("#ef4444","#fff")}>Delete</button>
                      {it.status !== 'deleted' && (
                        <button onClick={() => patchStatus(it.image_id, it.status === 'active' ? 'inactive' : 'active')} style={btn("#374151","#fff")}>
                          {it.status === 'active' ? 'Make Inactive' : 'Make Active'}
                        </button>
                      )}
                      {it.status !== 'deleted' && (
                        <button onClick={() => patchStatus(it.image_id, 'deleted')} style={btn("#6b7280","#fff")}>
                          Soft Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div style={pager}>
            <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} style={btn("#e5e7eb","#111")}>Prev</button>
            <span style={{ fontSize:13, color:"#374151" }}>Page {page} / {pagesCount} (total {total})</span>
            <button disabled={page>=pagesCount} onClick={()=>setPage(p=>p+1)} style={btn("#e5e7eb","#111")}>Next</button>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {editing && (
        <div style={modalBackdrop} onClick={()=>setEditing(null)}>
          <div style={modal} onClick={(e)=>e.stopPropagation()}>
            <h3 style={{ marginTop:0 }}>{editing.image_id ? "Edit Image" : "Add Image"}</h3>

            {/* scrollable body */}
            <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: 8, display:"grid", gap:10 }}>
              <label style={lbl}>Title
                <input value={editing.image_title || ""} onChange={(e)=>setEditing(s=>({...s, image_title:e.target.value}))} style={input}/>
              </label>
              <label style={lbl}>Alt
                <input value={editing.image_alt || ""} onChange={(e)=>setEditing(s=>({...s, image_alt:e.target.value}))} style={input}/>
              </label>

              <label style={lbl}>Image Path
                <input value={editing.image_path || ""} onChange={(e)=>setEditing(s=>({...s, image_path:e.target.value}))} style={input}/>
              </label>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <input ref={fileRef} type="file" accept="image/*,.svg,.ico" />
                <button onClick={uploadFile} disabled={uploading} style={btn("#059669","#fff")}>
                  {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>

              {/* Page/Subpage selects with names, storing IDs */}
              <div style={{ display:"grid", gap:10, gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))" }}>
                <label style={lbl}>Page
                  <select
                    value={editing.page_id || ""}
                    onChange={(e) => {
                      const newPid = e.target.value;
                      setEditing(s => ({ ...s, page_id: newPid, subpage_id: "" })); // reset subpage when page changes
                      loadSubpagesByPage(newPid ? Number(newPid) : null);
                    }}
                    style={input}
                  >
                    <option value="">— None —</option>
                    {pagesList.map(p => (
                      <option key={p.page_id} value={p.page_id}>{p.page_name}</option>
                    ))}
                  </select>
                </label>

                <label style={lbl}>Subpage
                  <select
                    value={editing.subpage_id || ""}
                    onChange={(e) => setEditing(s => ({ ...s, subpage_id: e.target.value }))}
                    style={input}
                    disabled={!editing.page_id}
                  >
                    <option value="">— None —</option>
                    {subpagesList.map(sp => (
                      <option key={sp.subpage_id} value={sp.subpage_id}>{sp.subpage_name}</option>
                    ))}
                  </select>
                </label>

             <label style={lbl}>
  Category
  <select
    value={editing.category ?? ""}
    onChange={(e) =>
      setEditing((s) => ({ ...s, category: e.target.value }))
    }
    style={input}
    required
  >
    <option value="" disabled>
      Select category…
    </option>
    <option value="carousel">carousel</option>
    <option value="banner">banner</option>
    <option value="card">card</option>
    <option value="gallery">gallery</option>
  </select>
</label>

                 

                <label style={lbl}>Status
                  <select value={editing.status || "active"} onChange={(e)=>setEditing(s=>({...s, status:e.target.value}))} style={input}>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                    <option value="deleted">deleted</option>
                  </select>
                </label>
              </div>

              {editing.image_path && (
                <div style={{ marginTop:8 }}>
                  <strong>Preview:</strong>
                  <div style={{ marginTop:8, border:"1px solid #e5e7eb", borderRadius:8, padding:8, background:"#fafafa" }}>
                    <img src={"https://gantasoft.in/rankridgeschool/dev"+editing.image_path} alt={editing.image_alt || editing.image_title} style={{ maxWidth:"100%", maxHeight:300, display:"block", margin:"0 auto" }}/>
                  </div>
                </div>
              )}
            </div>

            {/* footer */}
            <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"flex-end" }}>
              <button onClick={save} disabled={saving} style={btn("#16a34a","#fff")}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={()=>setEditing(null)} style={btn("#e5e7eb","#111")}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* styles */
const wrap = { maxWidth: 1200, margin:"0 auto", padding:16 };
const toolbar = { display:"flex", gap:8, alignItems:"center", margin:"12px 0 16px", flexWrap:"wrap" };
const grid = { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(340px, 1fr))", gap:12 };
const card = { border:"1px solid #e5e7eb", borderRadius:14, padding:14, background:"#fff" };
const input = { padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:10, minWidth:160 };
const btn = (bg, color) => ({ padding:"8px 12px", background:bg, color, border:"none", borderRadius:10, cursor:"pointer" });
const emptyBox = { border:"1px dashed #d1d5db", padding:24, borderRadius:12, color:"#6b7280", textAlign:"center" };
const pager = { display:"flex", alignItems:"center", gap:10, marginTop:16, justifyContent:"center" };
const thumb = { width:120, height:90, borderRadius:10, overflow:"hidden", background:"#f3f4f6", flexShrink:0, border:"1px solid #eef2f7" };
const img = { width:"100%", height:"100%", objectFit:"cover" };
const noimg = { fontSize:12, color:"#6b7280", display:"flex", alignItems:"center", justifyContent:"center", height:"100%", padding:8, textAlign:"center" };
const code = { background:"#f3f4f6", padding:"2px 6px", borderRadius:6 };
const chip = { fontSize:11, background:"#eef2ff", color:"#3730a3", padding:"2px 6px", borderRadius:999 };

const modalBackdrop = { position:"fixed", inset:0, background:"rgba(0,0,0,.35)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, zIndex:50 };
const modal = { width:"min(720px, 100%)", maxHeight:"90vh", overflowY:"auto", background:"#fff", borderRadius:14, padding:16, boxShadow:"0 10px 30px rgba(0,0,0,.2)" };
const lbl = { display:"grid", gap:6, fontSize:13, color:"#374151" };
